<!--
 * @Description: 申请开票-开票内容
 * @Date: 2025-05-20 15:57:04
-->
<template>
  <BaseForm
    ref="formRef"
    label-position="top"
    scrollToError
    :model="formData"
    :rules="formRules"
  >
    <!-- 表单标题 -->
    <div class="form-dialog__title">
      <h1>开票内容</h1>
    </div>

    <!-- 表单内容区域 -->
    <div class="form-dialog__content">
      <!-- 订单类型选择 -->
      <BaseFormItem
        label="订单类型"
        prop="stateType"
        :rules="[
          {
            required: true,
            message: '请选择',
            trigger: 'change',
            validator: (rule, v, callback) => {
              // 验证订单类型是否已选择
              if (!stateType) {
                callback(new Error('请选择'));
              } else {
                callback();
              }
            }
          }
        ]"
      >
        <BaseSelect
          class="form-dialog__w280"
          type="text"
          placeholder="请输入"
          v-model="stateType"
          :options="orderType"
          :disabled="disabled"
          @change="onStateTypeChange"
        />
      </BaseFormItem>

      <!-- 销售方选择 -->
      <BaseFormItem label="销售方" prop="theSellerId">
        <BaseSelect
          class="form-dialog__w280"
          placeholder="请选择"
          label-key="userDefinedName"
          value-key="userDefinedId"
          :disabled="!stateType || disabled"
          :init="false"
          :remoteMethod="stateTypeRemote"
          v-model="formData.theSellerId"
          @change="onTheSellerIdChange"
        />
      </BaseFormItem>

      <!-- 发票类型选择 -->
      <BaseFormItem label="发票类型" prop="invoiceType">
        <BaseSelect
          class="form-dialog__w280"
          type="text"
          placeholder="请输入"
          v-model="formData.invoiceType"
          :options="INVOICE_TYPE"
          :disabled="disabled"
          @change="onInvoiceTypeChange"
        />
      </BaseFormItem>

      <!-- 开票类目选择 -->
      <BaseFormItem label="开票类目" prop="itemId">
        <BaseSelect
          class="form-dialog__w280"
          type="text"
          placeholder="请选择"
          label-key="itemName"
          value-key="id"
          :disabled="!formData.theSellerId || !formData.invoiceType || disabled"
          :init="false"
          :remoteMethod="itemIdRemote"
          v-model="formData.itemId"
        />
      </BaseFormItem>

      <!-- 交易结构选择 -->
      <BaseFormItem
        label="交易结构"
        prop="taxPattern"
        v-if="IS_PLATFORM_TYPE_1"
      >
        <BaseSelect
          class="form-dialog__w280"
          type="text"
          placeholder="请选择"
          v-model="formData.taxPattern"
          :options="taxPatternMapping"
          :disabled="disabled"
        />
      </BaseFormItem>

      <!-- 支付通道选择 -->
      <BaseFormItem label="支付通道" prop="payPassagewayCode">
        <BaseSelect
          class="form-dialog__w280"
          type="text"
          placeholder="请选择"
          v-model="formData.payPassagewayCode"
          :options="PAY_PASSAGEWAY_MAPPING"
          :disabled="disabled"
        />
      </BaseFormItem>

      <!-- 选择日期范围 -->
      <BaseFormItem label="选择日期" prop="receiveAt">
        <BaseDatePicker
          class="form-dialog__w280"
          style="width: 280px"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          type="daterange"
          v-model="formData.receiveAt"
          :disabled="disabled"
        />
      </BaseFormItem>

      <!-- 付款方式选择 -->
      <BaseFormItem label="付款方式" prop="payType">
        <BaseSelect
          class="form-dialog__w280"
          type="text"
          placeholder="请选择"
          v-model="formData.payType"
          :options="PAY_TYPE"
          :disabled="disabled"
        />
      </BaseFormItem>
    </div>
  </BaseForm>
</template>

<script lang="tsx" setup>
import { ref, computed } from 'vue';
import {
  BaseForm,
  BaseFormItem,
  BaseDatePicker,
  BaseSelect
} from 'xrk-components';
import {
  MakeOutAnInvoiceParams,
  NotInvoicedListParams
} from '@V/invoice/src/data-source/types';
import { ORDER_TYPE, PAY_TYPE } from '@constants/order';
import {
  obtainTheSeller,
  invoiceCategory
} from '@V/invoice/src/data-source/apis';
import { taxPatternMapping } from '@constants/project';
import { PAY_PASSAGEWAY_MAPPING } from '@constants/bank';
import appStore from '@/store';
import { storeToRefs } from 'pinia';
import { INVOICE_TYPE } from '@V/invoice/src/constants';
import { OrderType } from '@/common/types/order';
import { IS_PLATFORM_TYPE_1, PLATFORM_IS_FEIGON } from '@/config';

// 定义表单数据类型，合并开票参数和未开票列表参数
type FormData = MakeOutAnInvoiceParams & NotInvoicedListParams;

// 组件属性定义
withDefaults(
  defineProps<{
    disabled: boolean; // 表单是否禁用
  }>(),
  {}
);

// 获取用户信息
const { info: userInfo } = storeToRefs(appStore.useUserStoreSetup);

// 表单数据模型绑定
// 注意：其中stateType使用销售方中返回数据，与订单类型无联动关系。
// 如果出现两个相同企业且其中stateType不一致，则会出现2条数据并且导致选择器异常：销售方唯一标识ID重复
const formData = defineModel<FormData>();

// 表单引用
const formRef = ref(null);

// 订单类型状态（独立于formData中的stateType）
// 与formData中的stateType可能不一样，也无联动关系。仅提供给销售方接口入参、交易结构required校验
const stateType = ref<MakeOutAnInvoiceParams['stateType']>(null);

// 表单验证规则
const formRules = computed(() => {
  return {
    // stateType: [{ required: true, message: '请选择', trigger: 'change' }],
    theSellerId: [{ required: true, message: '请选择', trigger: 'change' }],
    itemId: [{ required: true, message: '请选择', trigger: 'change' }],
    taxPattern: [
      {
        required: stateType.value === 1, // stateType为1（项目类型订单）必填，其他非必填
        message: '请选择',
        trigger: 'change'
      }
    ],
    invoiceType: [{ required: true, message: '请选择', trigger: 'change' }]
  };
});

type OrderTypeOption = { label: string; value: number };

const filterFeigonOrderType = <T extends OrderTypeOption>(orderTypes: T[]) => {
  if (!PLATFORM_IS_FEIGON) return orderTypes;
  return orderTypes.filter(item => ![4, 5].includes(item.value));
};

// 根据用户功能码计算可选的订单类型
const orderType = computed(() => {
  // 功能码为2时，只显示特定订单类型（1, 4, 5）
  if (userInfo.value.functionCode === 2) {
    return filterFeigonOrderType(
      Object.values(ORDER_TYPE).filter(item =>
        [1, 4, 5, 8].includes(item.value)
      )
    );
  }
  // 功能码为3时（供应商），只显示综合服务费选项（6）
  if (userInfo.value.functionCode === 3) {
    return Object.values(ORDER_TYPE).filter(item => [6].includes(item.value));
  }
  // 默认显示所有订单类型
  if (PLATFORM_IS_FEIGON)
    return filterFeigonOrderType(Object.values(ORDER_TYPE));
  return ORDER_TYPE;
});

// 获取销售方远程数据方法
const stateTypeRemote = () => {
  return obtainTheSeller({
    userDefinedOrderType: ([1, 2, 3, 7].includes(stateType.value)
      ? 1
      : stateType.value) as OrderType
  });
};

// 获取开票类目远程数据方法
const itemIdRemote = () => {
  return invoiceCategory({
    stateType: formData.value.stateType || stateType.value, // 优先使用选择的销售方中携带的类型数据
    invoiceType: formData.value.invoiceType,
    theSellerId: formData.value.theSellerId
  });
};

// 订单类型变更处理
const onStateTypeChange = v => {
  // 同步设置用户自定义订单类型
  formData.value.userDefinedOrderType = v;
  // 清空销售方选择
  formData.value.theSellerId = null;
  // 触发发票类型变更处理
  onInvoiceTypeChange();
};

// 销售方变更处理
const onTheSellerIdChange = (v, [option]) => {
  // 设置状态类型为选中销售方的状态类型
  formData.value.stateType = option.stateType;
  // 触发发票类型变更处理
  onInvoiceTypeChange();

  // 个体销售方项目时，交易结构写死个体产品5。运营提的
  formData.value.taxPattern = option.stateType === 7 ? 5 : null;
};

// 发票类型变更处理
const onInvoiceTypeChange = () => {
  // 清空开票类目选择
  formData.value.itemId = null;
};

// 暴露验证方法给父组件调用
defineExpose({
  validate: (...args: any) => formRef.value?.validate(...args)
});
</script>
